import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { IDocumentSubTypesService } from "services/DocumentSubTypesService";
import { IDocumentTypesService } from "services/DocumentTypesService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { DocumentSubTypeModel, DocumentTypeModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  DocumentSubTypes: new Array<DocumentSubTypeModel>(),
  LoadingDocumentSubTypes: false,
  EditingDocumentSubTypes: false,
  DocumentTypes: new Array<DocumentTypeModel>(),
  LoadingDocumentTypes: true,
  SavedColumnOrder: new Array<string>(),
};

export const documentSubTypesSlice = createSlice({
  name: "documentSubTypes",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getDocumentSubTypes.pending, (state) => {
        state.LoadingDocumentSubTypes = true;
      })
      .addCase(getDocumentSubTypes.fulfilled, (state, action) => {
        state.DocumentSubTypes = action.payload;
        state.LoadingDocumentSubTypes = false;
      })
      .addCase(getDocumentSubTypes.rejected, (state) => {
        state.LoadingDocumentSubTypes = false;
      })
      .addCase(createDocumentSubType.pending, (state) => {
        state.EditingDocumentSubTypes = true;
      })
      .addCase(updateDocumentSubType.pending, (state) => {
        state.EditingDocumentSubTypes = true;
      })
      .addCase(deleteDocumentSubType.pending, (state) => {
        state.EditingDocumentSubTypes = true;
      })
      .addCase(createDocumentSubType.rejected, (state) => {
        state.EditingDocumentSubTypes = false;
      })
      .addCase(updateDocumentSubType.rejected, (state) => {
        state.EditingDocumentSubTypes = false;
      })
      .addCase(deleteDocumentSubType.rejected, (state) => {
        state.EditingDocumentSubTypes = false;
      })
      .addCase(createDocumentSubType.fulfilled, (state) => {
        toast.success("Document Sub Type Created");
        state.EditingDocumentSubTypes = false;
      })
      .addCase(updateDocumentSubType.fulfilled, (state) => {
        toast.success("Document Sub Type Updated");
        state.EditingDocumentSubTypes = false;
      })
      .addCase(deleteDocumentSubType.fulfilled, (state) => {
        toast.success("Document Sub Type Deleted");
        state.EditingDocumentSubTypes = false;
      })
      .addCase(getDocumentTypes.pending, (state) => {
        state.LoadingDocumentTypes = true;
      })
      .addCase(getDocumentTypes.fulfilled, (state, action) => {
        state.DocumentTypes = action.payload;
        state.LoadingDocumentTypes = false;
      })
      .addCase(getDocumentTypes.rejected, (state) => {
        state.LoadingDocumentTypes = false;
      })
      .addCase(getDocumentSubTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateDocumentSubTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.meta.arg;
        toast.success("Column order saved");
      });
  },
});

export const getDocumentSubTypes = createAsyncThunk("DocumentSubTypes/GetDocumentSubTypes", async () => {
  const documentSubTypesService = container.get<IDocumentSubTypesService>(SERVICE_TYPES.DocumentSubTypesService);
  return await documentSubTypesService.getDocumentSubTypes();
});

export const createDocumentSubType = createAsyncThunk<
  void,
  { name: string; documentTypeID: number },
  { state: RootState }
>("DocumentSubTypes/CreateDocumentSubType", async ({ name, documentTypeID }, thunkAPI) => {
  const documentSubTypesService = container.get<IDocumentSubTypesService>(SERVICE_TYPES.DocumentSubTypesService);
  await documentSubTypesService.createDocumentSubType(name, documentTypeID);
  thunkAPI.dispatch(getDocumentSubTypes());
});

export const updateDocumentSubType = createAsyncThunk<
  void,
  { documentSubTypeModel: DocumentSubTypeModel },
  { state: RootState }
>("DocumentSubTypes/UpdateDocumentSubType", async ({ documentSubTypeModel }, thunkAPI) => {
  const documentSubTypesService = container.get<IDocumentSubTypesService>(SERVICE_TYPES.DocumentSubTypesService);
  await documentSubTypesService.updateDocumentSubType(documentSubTypeModel);
  thunkAPI.dispatch(getDocumentSubTypes());
});

export const deleteDocumentSubType = createAsyncThunk<void, { documentSubTypeID: number }, { state: RootState }>(
  "DocumentSubTypes/DeleteDocumentSubType",
  async ({ documentSubTypeID }, thunkAPI) => {
    const documentSubTypesService = container.get<IDocumentSubTypesService>(SERVICE_TYPES.DocumentSubTypesService);
    await documentSubTypesService.deleteDocumentSubType(documentSubTypeID);
    thunkAPI.dispatch(getDocumentSubTypes());
  }
);

export const getDocumentTypes = createAsyncThunk(
  "DocumentSubTypes/GetDocumentTypes",
  async (includeHidden: boolean) => {
    const documentTypesService = container.get<IDocumentTypesService>(SERVICE_TYPES.DocumentTypesService);
    return await documentTypesService.getDocumentTypes(includeHidden);
  }
);

export const getDocumentSubTypesTableColumnOrder = createAsyncThunk(
  "DocumentSubTypes/GetDocumentSubTypesTableColumnOrder",
  async () => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    return await userSettingsService.getColumnOrder(SETTING_TYPES.DocumentSubTypesTableColumnKey);
  }
);

export const updateDocumentSubTypesTableColumnOrder = createAsyncThunk(
  "DocumentSubTypes/UpdateDocumentSubTypesTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.DocumentSubTypesTableColumnKey);
  }
);

export const selectDocumentSubTypes = (state: RootState) => {
  return state.documentSubTypes.DocumentSubTypes;
};
export const selectLoadingDocumentSubTypes = (state: RootState) => {
  return state.documentSubTypes.LoadingDocumentSubTypes;
};
export const selectEditingDocumentSubTypes = (state: RootState) => {
  return state.documentSubTypes.EditingDocumentSubTypes;
};

export const selectDocumentTypes = (state: RootState) => {
  return state.documentSubTypes.DocumentTypes;
};
export const selectLoadingDocumentTypes = (state: RootState) => {
  return state.documentSubTypes.LoadingDocumentTypes;
};

export const selectSavedColumnOrder = (state: RootState) => {
  return state.documentSubTypes.SavedColumnOrder;
};

export default documentSubTypesSlice.reducer;
